/**
 * Workspace Runtime Service – Phase 3C.
 *
 * Orchestration layer for the Workspace Runtime.
 * Single public entry point: resolveAndLoad({ role })
 *
 * Responsibilities:
 *  1. Resolve the runtime source (workspace vs. legacy)
 *  2. Determine runtimeMode: 'draft-edit' for TA/admin, 'runtime' for operators
 *  3. Load draft version for draft-edit users, published for runtime users
 *  4. Normalize to RuntimeLayoutData
 *  5. Return { layoutData, runtimeContext }
 *
 * A resolve or version failure is returned as unavailable or assigned-missing.
 * Callers must not replace those results with placeholder buttons.
 */
import {
  resolveRuntimeSource,
  legacyRuntimeContext,
  workspaceRuntimeContext,
  isDraftEditRole,
  RUNTIME_MODE,
} from './workspaceResolverService';
import { normalizeRuntimeLayout, RUNTIME_SOURCE } from './workspaceLayoutAdapter';
import { getPublishedVersion, getDraftVersion } from '../../api/workspaceApi';

export { RUNTIME_SOURCE, RUNTIME_MODE };

/** A published Home is not a draft. The first edit creates a draft from revision 0. */
export function draftSaveRevision(version, draftLoaded) {
  if (!draftLoaded) return 0;
  const revision = Number(version?.revision);
  return Number.isInteger(revision) && revision >= 0 ? revision : 0;
}

/**
 * @typedef {import('./workspaceResolverService').RuntimeContext} RuntimeContext
 * @typedef {import('./workspaceLayoutAdapter').RuntimeLayoutData} RuntimeLayoutData
 */

/**
 * @typedef {Object} RuntimeLoadResult
 * @property {RuntimeLayoutData}  layoutData     – normalized layout ready for renderer
 * @property {RuntimeContext}     runtimeContext – source + mode metadata
 * @property {boolean}            usedWorkspace  – true if workspace version was loaded
 */

/**
 * Load the published version for a workspace.
 * Returns null on any failure; caller falls back to legacy.
 */
const loadPublishedVersion = async (workspace) => {
  if (!workspace?.id || !workspace?.publishedVersionId) {
    console.warn('[WorkspaceRuntimeService] Workspace has no publishedVersionId – falling back to legacy', {
      workspaceId: workspace?.id,
    });
    return null;
  }
  try {
    const resp    = await getPublishedVersion(workspace.id);
    const version = resp?.data ?? resp;
    if (!version) {
      console.warn('[WorkspaceRuntimeService] Published version is empty – falling back to legacy', {
        workspaceId: workspace.id,
      });
      return null;
    }
    return version;
  } catch (err) {
    console.warn('[WorkspaceRuntimeService] Failed to load published version – falling back to legacy', {
      workspaceId: workspace?.id,
      error:       err?.message,
    });
    return null;
  }
};

/**
 * Load the current draft version for a workspace.
 * Returns null if no draft exists or on any error; caller falls through to published.
 */
const loadDraftVersion = async (workspace) => {
  if (!workspace?.id || !workspace?.currentDraftVersionId) return null;
  try {
    const resp    = await getDraftVersion(workspace.id);
    const version = resp?.data ?? resp;
    return version || null;
  } catch (err) {
    console.warn('[WorkspaceRuntimeService] Failed to load draft version – falling through to published', {
      workspaceId: workspace?.id,
      error:       err?.message,
    });
    return null;
  }
};

/**
 * Resolve the runtime source and load the appropriate layout.
 *
 * @param {{ role?: string|null }} [options]
 * @returns {Promise<RuntimeLoadResult>}
 */
export const resolveAndLoad = async ({ role = null } = {}) => {
  // Step 1 – Resolve runtime source
  let resolution;
  try {
    resolution = await resolveRuntimeSource();
  } catch (err) {
    console.warn('[WorkspaceRuntimeService] resolveRuntimeSource threw unexpectedly – legacy fallback', err);
    return {
      status: 'unavailable',
      layoutData: null,
      runtimeContext: legacyRuntimeContext(),
      usedWorkspace: false,
    };
  }

  // Step 2 – Legacy path: nothing to do, caller uses existing load
  if (resolution.status === 'unavailable') {
    return {
      status: 'unavailable',
      layoutData: null,
      runtimeContext: resolution.runtimeContext,
      usedWorkspace: false,
    };
  }

  if (resolution.useLegacy) {
    return {
      status: 'legacy',
      layoutData: null,
      runtimeContext: resolution.runtimeContext,
      usedWorkspace: false,
    };
  }

  const workspace   = resolution.workspace;
  const editMode    = isDraftEditRole(role);
  const runtimeMode = editMode ? RUNTIME_MODE.DRAFT_EDIT : RUNTIME_MODE.RUNTIME;

  // Step 3 – Load appropriate version
  // draft-edit users: try draft first, fall through to published if no draft
  // runtime users: always load published
  let version     = null;
  let draftLoaded = false;
  if (editMode) {
    version = await loadDraftVersion(workspace);
    if (version) {
      draftLoaded = true;
      console.debug('[WorkspaceRuntimeService] Loaded draft version for draft-edit user', {
        workspaceId: workspace.id,
        versionId:   version.id,
      });
    }
  }
  if (!version) {
    version = await loadPublishedVersion(workspace);
  }

  if (!version) {
    return {
      status: 'assigned-missing',
      layoutData: null,
      runtimeContext: workspaceRuntimeContext(workspace),
      usedWorkspace: false,
    };
  }

  // Step 4 – Normalize layout
  const rawSchema  = version.layoutSchema || {};
  const layoutData = normalizeRuntimeLayout(RUNTIME_SOURCE.WORKSPACE, rawSchema);

  const runtimeContext = workspaceRuntimeContext(
    { ...workspace, revision: draftSaveRevision(version, draftLoaded) },
    version.id,
    runtimeMode,
    draftLoaded,
  );

  console.info('[WorkspaceRuntimeService] Workspace layout loaded', {
    workspaceId:  runtimeContext.workspaceId,
    versionId:    runtimeContext.workspaceVersionId,
    runtimeMode,
    iconCount:    layoutData.icons.length,
  });

  return { status: 'ready', layoutData, runtimeContext, usedWorkspace: true };
};
